import { lazy, Suspense, useEffect, type ComponentType } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { LeagueProvider, useLeague } from './contexts/LeagueContext'
import { SportProvider } from './contexts/SportContext'
import { usePageTracking } from './hooks/usePageTracking'
import ClosedPage from './components/ClosedPage'
import SubscriptionInactivePage from './components/SubscriptionInactivePage'
import FanAiBubble from './components/fan-ai/FanAiBubble'
import ScoreLmBadge from './components/ScoreLmBadge'
import { AddonRoute } from './components/RouteGates'
import { publicGateFor } from './contexts/tenantAccess'

// The public site renders through the website-template runtime
// (src/site/core, ARCHITECTURE §5–§6): SiteTemplateHost serves every
// templated route; classic (the default template) is eager, with today's
// eager/lazy page split. Only the template-independent core pages below are
// routed here directly (D7).
import { SiteTemplateHost } from './site/core/template/SiteTemplateHost'
import { DEV_OVERRIDE_ENABLED } from './site/core/template/devOverride'
import { STANDALONE_ROUTES, type StandalonePageKey } from './site/core/routes'
import BootLoader from './site/core/states/BootLoader'
import { getDeviceLocale, rememberLangParam, resolveLocale, useApplyLocale } from './i18n'
import CoreNotFound from './site/core/states/CoreNotFound'

const ScoreBugOverlay = lazy(() => import('./pages/ScoreBugOverlay'))
// Ticketing core pages: the branded ticket page, the order wallet (every
// ticket on an N>1 order at one URL, sent by email/SMS) and the post-purchase
// landing Stripe Checkout redirects to (polls until the webhook has issued
// tickets). Lazy-loaded; most fans never hit them in a session.
const TicketPage = lazy(() => import('./pages/TicketPage'))
const OrderWalletPage = lazy(() => import('./pages/OrderWalletPage'))
const PurchaseSuccessPage = lazy(() => import('./pages/PurchaseSuccessPage'))

const STANDALONE_PAGES: Record<StandalonePageKey, ComponentType> = {
  ticket: TicketPage,
  orderWallet: OrderWalletPage,
  purchaseSuccess: PurchaseSuccessPage,
  scoreBugOverlay: ScoreBugOverlay,
}

// Dev / staging only: the template workbench (§10.4), outside the host.
const Workbench = DEV_OVERRIDE_ENABLED ? lazy(() => import('./site/core/workbench/Workbench')) : null

// Admin - lazy loaded. /admin/* is the league admin (src/admin-v2): its
// router serves each area's manifest routes, and old v1 URLs redirect to
// their v2 homes (admin-v2/app/legacyRedirects.ts).
const AdminV2 = lazy(() => import('./admin-v2/app/AdminV2'))
const Login = lazy(() => import('./admin-v2/app/auth/Login'))
const ImpersonateLanding = lazy(() => import('./admin-v2/app/auth/ImpersonateLanding'))
const InviteLanding = lazy(() => import('./admin-v2/app/auth/InviteLanding'))
const ForgotPassword = lazy(() => import('./admin-v2/app/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./admin-v2/app/auth/ResetPassword'))
const HandoffLanding = lazy(() => import('./admin-v2/app/auth/HandoffLanding'))

function AppRoutes() {
  // Track page views for usage analytics (must be inside BrowserRouter for useLocation)
  usePageTracking()
  const location = useLocation()
  // Don't render the fan AI bubble on admin or OBS overlay routes — they
  // have their own UI shells, and the bubble would clutter live broadcasts.
  const showFanAi =
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/obs/')
  // Same exclusions as the fan AI bubble: keep the attribution badge off
  // admin shells and chrome-free OBS broadcast overlays. The per-tenant
  // corner_badge flag (default off) gates it inside the component.
  const showBadge =
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/obs/')

  return (
    <>
      <Routes>
        {/* Template-independent core pages (D7): the
            ticket / wallet / purchase-success pages and the OBS overlay.
            /tickets/t/:token etc. out-rank the host's /tickets/:slug by
            specificity (test/site/contract/routes.parity.test.ts). */}
        {STANDALONE_ROUTES.map((r) => {
          const Page = STANDALONE_PAGES[r.page]
          return (
            <Route
              key={r.path}
              path={r.path}
              element={r.addon ? <AddonRoute addon={r.addon}><Page /></AddonRoute> : <Page />}
            />
          )
        })}

        {/* Admin Routes. AdminV2 owns /admin, /admin/league/* and
            /admin/:sport/*; login, impersonate and handoff keep their URLs.
            The signed-out staff pages (the staff invite landing, forgot and
            reset password) sit beside login, outside AdminV2's sign-in gate.
            Being under /admin keeps their tokens out of usePageTracking, the
            Fan AI bubble, publicGateFor and the custom-domain redirect.
            There is one staff reset, 1.0's: the email links to
            /admin/reset-password#<token> (the sign-in page's "Forgot your
            password?" and Settings › Your account both ask for it). */}
        <Route path="/admin/login" element={<Login />} />
        <Route path="/admin/impersonate" element={<ImpersonateLanding />} />
        <Route path="/admin/invite/:token" element={<InviteLanding />} />
        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        <Route path="/admin/handoff" element={<HandoffLanding />} />
        <Route path="/admin/*" element={<AdminV2 />} />

        {Workbench && <Route path="/__templates" element={<Workbench />} />}

        {/* Every other public URL: the active website template. */}
        <Route path="/*" element={<SiteTemplateHost />} />
      </Routes>
      {showFanAi && <FanAiBubble />}
      {showBadge && <ScoreLmBadge />}
    </>
  )
}

/**
 * The public site's language (#1063): ?lang=, then this device's choice, then
 * the league's default. The league admin applies its own (admin-v2/app/AdminV2:
 * the admin's preference first), so /admin/* is left to it; its login,
 * impersonate, handoff, staff invite and reset pages have no session yet and
 * follow the site's rule.
 */
function useSiteLocale(leagueDefault: string | null | undefined, enabled: string[] | undefined): boolean {
  const path = window.location.pathname
  const signedOut = ['/admin/login', '/admin/impersonate', '/admin/handoff', '/admin/forgot-password', '/admin/reset-password', '/admin/invite/']
  const adminOwned = path.startsWith('/admin') && !signedOut.some((p) => path.startsWith(p))
  const search = window.location.search
  // Only the languages the league turned on (English only by default, #1063).
  const offered = enabled?.length ? enabled : [leagueDefault ?? 'en']
  const offeredKey = offered.join(',')
  // Remember a ?lang= link on the device once (it's read straight from the URL below too).
  useEffect(() => {
    if (!adminOwned) rememberLangParam(search, offeredKey.split(','))
  }, [adminOwned, search, offeredKey])
  const target = resolveLocale({ search, device: getDeviceLocale(), leagueDefault, enabled: offered })
  const ready = useApplyLocale(target)
  return adminOwned || ready
}

function AppContent() {
  const { league, isLoading, error } = useLeague()
  const localeReady = useSiteLocale(league?.default_locale, league?.enabled_locales)

  // OBS overlay routes are tenant-agnostic — the score bug UUID is the only
  // identifier that matters, and OBS sources are commonly loaded from LAN IPs
  // or other hosts that don't resolve to a tenant. Skip the tenant gates so
  // they keep rendering even when the host has no league.
  const onObsPath = window.location.pathname.startsWith('/obs/')

  if ((isLoading && !league && !onObsPath) || !localeReady) {
    return <BootLoader />
  }

  if (error && !league && !onObsPath) {
    return <CoreNotFound />
  }

  // Account status (switch model), never applied to /admin paths:
  //   - suspended (status 'suspended', or the legacy is_active=false the
  //     backend keeps in sync) → on-brand closed page. The backend locks
  //     every public route, Fan AI included, in this state.
  //   - subscription not live → "paused" page. The admin stays reachable so
  //     the league can still log in and pay. A missing subscription field
  //     defaults GRACEFULLY to live (older payloads).
  const gate = publicGateFor(league, window.location.pathname)
  if (gate === 'closed') return <ClosedPage />
  if (gate === 'inactive') return <SubscriptionInactivePage />

  return (
    <BrowserRouter>
      <Suspense fallback={<BootLoader />}>
        <AppRoutes />
      </Suspense>
    </BrowserRouter>
  )
}

function App() {
  return (
    <LeagueProvider>
      <SportProvider>
        <ThemeProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ThemeProvider>
      </SportProvider>
    </LeagueProvider>
  )
}

export default App
